// Copyright 2023, mochabug AB
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { exec } from 'child_process';
import fs from 'fs';
import { mkdirp } from 'mkdirp';
import path from 'path';
import util from 'util';
import { VertexMetadataJson } from './genproto/mochabugapis/adapt/graph/v1/vertex_metadata_pb';
import {
  CapabilityJson,
  VertexJson
} from './genproto/mochabugapis/adapt/plugins/v1/vertex_pb';
import { labelize, readAsset, writeFile } from './utils';

const execPromise = util.promisify(exec);

export async function addVertex(
  cwd: string,
  name: string,
  folder: string,
  capabilities: CapabilityJson[],
  frontendLib?: 'none' | 'react' | undefined
) {
  if (fs.existsSync(folder)) {
    throw new Error(`The folder for the new vertex: ${folder} already exists`);
  }
  mkdirp.sync(folder);
  const execFile = path.join(folder, 'executor.ts');

  // Observe that we, by default, don't set the metadata.json here since we assume that it's located next to the vertex.json by default
  // However, we will search for the metadata.json by checking the metadata field and then check for the metadata.json
  const vertex: VertexJson = {
    name,
    label: labelize(name),
    description: `The better description of what ${name} is doing, the better it is for both AI and humans`,
    capabilities,
    metadata: 'metadata.json',
    ...(capabilities.includes('CAPABILITY_CONFIGURATOR')
      ? {
          vertexConfig: {
            generation: 'default',
            wipe: {}
          }
        }
      : {}),
    configStoreVertex: {
      generation: 'default',
      wipe: {}
    }
  };

  // Write the UI files
  const uiPath = path.join(folder, 'ui');
  if (frontendLib === 'react') {
    await execPromise('npm install react react-dom', {
      cwd: cwd
    });
    await execPromise('npm install --save-dev @types/react @types/react-dom', {
      cwd: cwd
    });
    if (capabilities.includes('CAPABILITY_CONFIGURATOR')) {
      writeFile(
        path.join(uiPath, 'configurator.App.tsx'),
        readAsset('configurator.App.tsx')
      );
      writeFile(
        path.join(uiPath, 'configurator.main.tsx'),
        readAsset('configurator.main.tsx')
      );
      writeFile(
        path.join(uiPath, 'configurator.ssg.tsx'),
        readAsset('configurator.ssg.tsx')
      );
      writeFile(
        path.join(uiPath, 'configurator.html'),
        readAsset('configurator.html')
      );
    }
    if (capabilities.includes('CAPABILITY_BROWSER')) {
      writeFile(
        path.join(uiPath, 'executor.App.tsx'),
        readAsset('executor.App.tsx')
      );
      writeFile(
        path.join(uiPath, 'executor.main.tsx'),
        readAsset('executor.main.tsx')
      );
      writeFile(
        path.join(uiPath, 'executor.ssg.tsx'),
        readAsset('executor.ssg.tsx')
      );
      writeFile(path.join(uiPath, 'executor.html'), readAsset('executor.html'));
    }
  } else if (frontendLib === 'none') {
    if (capabilities.includes('CAPABILITY_CONFIGURATOR')) {
      writeFile(
        path.join(uiPath, 'configurator.main.ts'),
        readAsset('configurator_browser_none.main.ts')
      );
      writeFile(
        path.join(uiPath, 'configurator.ssg.ts'),
        readAsset('configurator_browser_none.ssg.ts')
      );
      writeFile(
        path.join(uiPath, 'configurator.html'),
        readAsset('configurator.html')
      );
    }
    if (capabilities.includes('CAPABILITY_BROWSER')) {
      writeFile(
        path.join(uiPath, 'executor.main.ts'),
        readAsset('executor_browser_none.main.ts')
      );
      writeFile(
        path.join(uiPath, 'executor.ssg.ts'),
        readAsset('executor_browser_none.ssg.ts')
      );
      writeFile(path.join(uiPath, 'executor.html'), readAsset('executor.html'));
    }
  }

  if (capabilities.includes('CAPABILITY_BROWSER')) {
    if (capabilities.includes('CAPABILITY_CRON')) {
      if (frontendLib === 'react') {
        writeFile(
          execFile,
          readAsset('executor_browser_react_cron.tsx').replaceAll(
            '___VERTEX_NAME___',
            name
          )
        );
      } else {
        writeFile(
          execFile,
          readAsset('executor_browser_none_cron.ts').replaceAll(
            '___VERTEX_NAME___',
            name
          )
        );
      }
    } else {
      if (frontendLib === 'react') {
        writeFile(
          execFile,
          readAsset('executor_browser_react.tsx').replaceAll(
            '___VERTEX_NAME___',
            name
          )
        );
      } else {
        writeFile(
          execFile,
          readAsset('executor_browser_none.ts').replaceAll(
            '___VERTEX_NAME___',
            name
          )
        );
      }
    }
  } else if (capabilities.includes('CAPABILITY_EXTERNAL')) {
    if (capabilities.includes('CAPABILITY_CRON')) {
      writeFile(execFile, readAsset('executor_external_cron.ts'));
    } else {
      writeFile(execFile, readAsset('executor_external.ts'));
    }
  } else {
    if (capabilities.includes('CAPABILITY_CRON')) {
      writeFile(execFile, readAsset('executor_action_cron.ts'));
    } else {
      writeFile(execFile, readAsset('executor_action.ts'));
    }
  }

  const metadata: VertexMetadataJson = {
    complete: true,
    receivers: [
      {
        name: 'input',
        description: 'Default input receiver',
        bindings: []
      }
    ],
    transmitters: [
      {
        name: 'output',
        description: 'Default output transmitter',
        signals: []
      }
    ],
    exchanges: []
  };

  // Runs every 5 seconds
  if (capabilities.includes('CAPABILITY_CRON')) {
    metadata.cronSchedule = '*/5 * * * * *';
  }

  if (capabilities.includes('CAPABILITY_CONFIGURATOR')) {
    if (frontendLib === 'react') {
      writeFile(
        path.join(folder, 'configurator.ts'),
        readAsset('configurator_react.tsx').replaceAll(
          '___VERTEX_NAME___',
          name
        )
      );
    } else {
      writeFile(
        path.join(folder, 'configurator.ts'),
        readAsset('configurator_none.ts').replaceAll(
          '___VERTEX_NAME___',
          name
        )
      );
    }

    // If we have a config, make sure we don't say that the config is complete by default
    metadata.complete = false;
  }

  // Write the config json file
  fs.writeFileSync(
    path.join(folder, 'metadata.json'),
    JSON.stringify(metadata, null, 2)
  );

  // Write the vertex.json as well
  fs.writeFileSync(
    path.join(folder, 'vertex.json'),
    JSON.stringify(vertex, null, 2)
  );

  return vertex;
}
